import React, { useState } from 'react';
import '../styles/components/SpeechItem.css';
import '../styles/common/moments.css';
import { api, postData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { getMomentStyling } from '../utils/momentUtils';

interface SpeechItemProps {
  id: string;
  startMs: number;
  endMs?: number;
  speech: string | (string | React.ReactElement)[];
  speaker: { id: number; name: string };
  moments: Array<{ id: number; name: string; sentiment_id?: number; sentiment_name?: string }>;
  isActive: boolean;
  transcriptId: number;
  remark: string;
  reviewer: string;
  onClick: (id: string) => void;
  onMouseEnter: (id: string) => void;
}

const SpeechItem: React.FC<SpeechItemProps> = ({
  id,
  startMs,
  endMs,
  speech,
  speaker,
  moments,
  isActive,
  transcriptId,
  remark,
  reviewer,
  onClick,
  onMouseEnter
}) => {
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState(remark || '');

  // Format time as MM:SS
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleCommentSubmit = async (e?: React.FormEvent, commentText?: string) => {
    if (e) e.preventDefault();
    
    // Use provided commentText or current comment state
    const remarkText = commentText !== undefined ? commentText : comment.trim();
    
    try {
      const url = api(PATH_SEGMENTS.SPEECHTEXT, PATH_SEGMENTS.REMARK) + '/';
      await postData(url, {
        sid: parseInt(id),
        remark: remarkText
      }, 'PUT');
      // Reload the page to show updated comment
      window.location.reload();
      setShowCommentBox(false);
    } catch (error) {
      console.error('Failed to submit comment:', error);
    }
  };

  const handleSpeakerClick = (userId: number) => {
    if (speaker.name.toLowerCase() === 'agent') {
      window.open(api(PATH_SEGMENTS.USER, userId.toString()), '_blank');
    }
  };

  const speakerType = speaker.name.toLowerCase() === 'agent' ? 'agent' : 'customer';

  return (
    <div 
      id={id}
      className={`speech-item ${speakerType} ${isActive ? 'active' : ''}`}
      onClick={() => onClick(id)}
      onMouseEnter={() => onMouseEnter(id)}
      onFocus={() => console.log("onFocus: id", id)}
    >
      <div className="speech-header">
        <div className="speech-info">
          <div 
            className="speech-timestamp" 
            title={`${formatTime(startMs)}`}
          >
            {formatTime(startMs)}
          </div>
          <div 
            className={`speech-speaker ${speakerType === 'agent' ? 'clickable' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleSpeakerClick(speaker.id);
            }}
          >
            {speaker.name}
          </div>
          
          {moments?.length > 0 && (
            <div className="speech-tags">
              {moments.map((tag, tagIndex) => (
                <span key={tagIndex} className={`moment-tag moment-tag-full ${getMomentStyling(tag.sentiment_id, tag.sentiment_name, true).className}`}>
                  {getMomentStyling(tag.sentiment_id, tag.sentiment_name, true).icon && (
                    <span className="material-symbols-outlined moment-tag-icon">
                      {getMomentStyling(tag.sentiment_id, tag.sentiment_name, true).icon}
                    </span>
                  )}
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
        
        <div className="speech-item-controls">
          <button 
            className="icon-button play-button" 
            onClick={(e) => {
              e.stopPropagation();
              onClick(id);
            }}
            aria-label="Play audio segment"
            title="Play audio segment"
          >
            <span className="material-icons play-icon">▶️</span>
          </button>
        </div>
      </div>
      
      <div className="speech-text">{speech}</div>
      
      <div className="speech-comment-section">
        {!remark && !showCommentBox && (
          <button 
            className="icon-button comment-button" 
            onClick={(e) => {
              e.stopPropagation();
              setShowCommentBox(true);
            }}
            aria-label="Add comment"
            title="Add comment"
          >
            <span className="material-icons speech-bubble-icon">💬</span>
          </button>
        )}
        
        {remark && !showCommentBox && (
          <div className="speech-comments">
            <div className="speech-comment">
              <div className="comment-content">
                <div className="comment-text-container">
                  <p className="comment-text">{remark}</p>
                  <p className="reviewer-name">- {reviewer}</p>
                </div>
                <div className="comment-actions">
                  <button 
                    className="icon-button edit-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCommentBox(true);
                    }}
                    title="Edit comment"
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button 
                    className="icon-button delete-button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this comment?')) {
                        handleCommentSubmit(undefined, '');
                      }
                    }}
                    title="Delete comment"
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      
      {showCommentBox && (
        <form className="comment-form" onSubmit={handleCommentSubmit}>
          <textarea
            className="comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={remark ? "Update comment..." : "Add a comment..."}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="comment-actions">
            <button 
              type="button" 
              className="icon-button cancel-button"
              onClick={(e) => {
                e.stopPropagation();
                setComment(remark || ''); // Reset to original comment if canceling
                setShowCommentBox(false);
              }}
              title="Cancel"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            <button 
              type="submit" 
              className="icon-button save-button"
              disabled={!comment.trim() || comment === remark}
              title={remark ? 'Update comment' : 'Save comment'}
            >
              <span className="material-symbols-outlined">save</span>
            </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
};

export default SpeechItem; 